/**
 * Renders an Ask Dink answer's markdown (DNKADM-0434) — a deliberately small,
 * SAFE subset, with no markdown dependency and no `dangerouslySetInnerHTML`.
 *
 * The answer is model output, so it is parsed into React elements and every piece
 * of text goes through React's own escaping: raw HTML in the answer renders as
 * literal text, never markup. Supported: paragraphs, `#` headings (shown as bold
 * lines), `-`/`*`/`+` bullet lists, `1.` numbered lists, `**bold**`, `*italic*` /
 * `_italic_` and `` `code` ``. A markdown link `[text](url)` renders as its TEXT
 * only — the answer's navigation is the separate link buttons, which are checked
 * against the caller's screens; an inline URL never becomes a clickable link.
 *
 * Backend text: the whole answer is brand-scrubbed (`scrubBrand`) before parsing.
 */
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { MONO } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] };

const HEADING = /^#{1,6}\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]*\)|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;

/** Split the answer into blocks. */
function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  let current = null as Block | null;
  for (const raw of markdown.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      current = null;
      continue;
    }
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (heading) {
      blocks.push({ kind: 'h', text: heading[1] ?? '' });
      current = null;
    } else if (bullet || numbered) {
      const kind = bullet ? 'ul' : 'ol';
      const text = (bullet ?? numbered)?.[1] ?? '';
      if (current?.kind === kind) {
        current.items.push(text);
      } else {
        current = { kind, items: [text] };
        blocks.push(current);
      }
    } else if ((current?.kind === 'ul' || current?.kind === 'ol') && /^\s+/.test(raw)) {
      // An indented continuation of the previous list item.
      const last = current.items.length - 1;
      current.items[last] = `${current.items[last] ?? ''} ${line.trim()}`;
    } else if (current?.kind === 'p') {
      current.lines.push(line.trim());
    } else {
      current = { kind: 'p', lines: [line.trim()] };
      blocks.push(current);
    }
  }
  return blocks;
}

function renderInline(text: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const key = `${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <Box key={key} component="code" sx={{ fontFamily: MONO, fontSize: '0.9em' }}>
          {part.slice(1, -1)}
        </Box>
      );
    }
    const link = /^\[([^\]]+)\]\([^)]*\)$/.exec(part);
    if (link) return link[1];
    if (
      part.length > 2 &&
      ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_')))
    ) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

export function AnswerMarkdown({ markdown }: { markdown: string }) {
  const blocks = parseBlocks(scrubBrand(markdown) ?? '');
  return (
    <Box sx={{ fontSize: 14, '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 } }}>
      {blocks.map((block, i) => {
        const key = `${block.kind}-${i}`;
        if (block.kind === 'h') {
          return (
            <Typography key={key} component="p" sx={{ fontWeight: 600, fontSize: 14, my: 1 }}>
              {renderInline(block.text)}
            </Typography>
          );
        }
        if (block.kind === 'p') {
          return (
            <Typography key={key} component="p" sx={{ fontSize: 14, my: 1 }}>
              {renderInline(block.lines.join(' '))}
            </Typography>
          );
        }
        return (
          <Box key={key} component={block.kind} sx={{ pl: 2.5, my: 1 }}>
            {block.items.map((item, j) => (
              <Typography key={`${key}-${j}`} component="li" sx={{ fontSize: 14, mb: 0.5 }}>
                {renderInline(item)}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
